import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, TextField, Select, MenuItem, FormControl, InputLabel, Pagination, Box, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon from '@mui/icons-material/Add';


const ProductList = ({ onAdd, onEdit, onDelete, products: initialProducts, loading, token, onFavoriteToggle }) => {
  const [dealers, setDealers] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [products, setProducts] = useState(initialProducts);

  // Keep products in sync with parent prop
  useEffect(() => { setProducts(initialProducts); }, [initialProducts]);

  useEffect(() => {
    if (!token) return;
    fetch('/api/dealers', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setDealers(Array.isArray(data) ? data : []));
  }, [token]);


  // Helper to get dealer name from product (populated or id)
  const getDealerName = (dealer) => {
    if (!dealer) return '';
    if (typeof dealer === 'object' && dealer.name) return dealer.name;
    // fallback: lookup by id
    const found = dealers.find(d => d._id === dealer);
    return found ? found.name : '';
  };


  // Filter products by search (now includes itemNumber)
  const filteredProducts = products.filter(product => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (product.name && product.name.toLowerCase().includes(q)) ||
      (product.brand && product.brand.toLowerCase().includes(q)) ||
      (product.category && product.category.toLowerCase().includes(q)) ||
      (product.itemNumber && product.itemNumber.toLowerCase().includes(q))
    );
  });

  // Sort: favorites first, then by name
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (a.favorite === b.favorite) {
      return a.name.localeCompare(b.name);
    }
    return b.favorite - a.favorite;
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedProducts.length / pageSize) || 1;
  const pagedProducts = sortedProducts.slice((page - 1) * pageSize, page * pageSize);

  // Reset to page 1 if search or pageSize changes
  useEffect(() => { setPage(1); }, [search, pageSize]);

  // Calculate summary stats
  const lowStockCount = products.filter(p => p.stock < 10).length;
  const outOfStockCount = products.filter(p => p.stock === 0).length;
  const totalValue = products.reduce((sum, p) => sum + (p.price * p.stock), 0);

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', bgcolor: '#f0f2f5', display: 'flex', overflow: 'hidden' }}>
      {/* Left Side - Products List */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '2px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2 }}>
          <InventoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              Inventory
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filteredProducts.length} products in stock
            </Typography>
          </Box>
        </Box>

        {/* Search & Filters */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Search Inventory"
              variant="outlined"
              size="small"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, brand, category, or SKU..."
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="page-size-label">Show</InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                label="Show"
                onChange={e => setPageSize(Number(e.target.value))}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'white' }}>
          <TableContainer>
            <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Favorite</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Dealer</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8}>Loading...</TableCell></TableRow>
            ) : pagedProducts.length === 0 ? (
              <TableRow><TableCell colSpan={8}>No products found.</TableCell></TableRow>
            ) : (
              pagedProducts.map((product) => (
                <TableRow key={product._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{product.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label={product.favorite ? 'Unmark as favorite' : 'Mark as favorite'}
                      onClick={async () => {
                        if (typeof product._id === 'string' && token) {
                          const updatedProduct = {
                            itemNumber: product.itemNumber,
                            name: product.name,
                            brand: product.brand,
                            category: product.category,
                            price: product.price,
                            stock: product.stock,
                            description: product.description,
                            dealer: product.dealer?._id || product.dealer || null,
                            favorite: !product.favorite,
                          };
                          await fetch(`/api/products/${product._id}`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify(updatedProduct),
                          });
                          setProducts(prev => prev.map(p => p._id === product._id ? { ...p, favorite: !p.favorite } : p));
                          if (onFavoriteToggle) onFavoriteToggle(product._id);
                        }
                      }}
                      sx={{ color: product.favorite ? 'gold' : 'grey.400' }}
                    >
                      {product.favorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{product.brand}</TableCell>
                  <TableCell>{product.category}</TableCell>
                  <TableCell>${product.price?.toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={product.stock} 
                      size="small"
                      color={product.stock === 0 ? 'error' : product.stock < 10 ? 'warning' : 'success'}
                      variant={product.stock === 0 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {product.description}
                  </TableCell>
                  <TableCell>{getDealerName(product.dealer)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => onEdit(product)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => onDelete(product)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
        </Box>

        {/* Pagination */}
        <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center' }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      </Box>

      {/* Right Side - Summary Panel */}
      <Paper 
        elevation={3}
        sx={{ 
          width: { xs: '100%', md: 400 }, 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 0,
          borderLeft: '2px solid #e0e0e0'
        }}
      >
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          bgcolor: '#1976d2', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <InventoryIcon />
          <Typography variant="h6" fontWeight={700}>
            Inventory Summary
          </Typography>
        </Box>

        {/* Summary Stats */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#fafafa', p: 2 }}>
          <Stack spacing={2}>
            {/* Total Products */}
            <Paper sx={{ p: 2.5, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                Total Products
              </Typography>
              <Typography variant="h3" fontWeight={700} color="primary" sx={{ mt: 1 }}>
                {products.length}
              </Typography>
            </Paper>

            {/* Total Value */}
            <Paper sx={{ p: 2.5, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                Total Inventory Value
              </Typography>
              <Typography variant="h4" fontWeight={700} color="success.main" sx={{ mt: 1 }}>
                ${totalValue.toFixed(2)}
              </Typography>
            </Paper>

            {/* Stock Alerts */}
            <Paper sx={{ p: 2.5, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 2, display: 'block' }}>
                Stock Alerts
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#ffebee', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Out of Stock</Typography>
                  <Chip label={outOfStockCount} color="error" size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Low Stock (&lt; 10)</Typography>
                  <Chip label={lowStockCount} color="warning" size="small" />
                </Box>
              </Stack>
            </Paper>

            {/* Quick Actions */}
            <Paper sx={{ p: 2.5, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 2, display: 'block' }}>
                Quick Actions
              </Typography>
              <Stack spacing={1}>
                <Button variant="contained" fullWidth startIcon={<AddIcon />} onClick={onAdd}>
                  Add New Product
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProductList;
